import styles from "./document-card.module.scss";
import pdf from "assets/images/v2/Doc_Primary.svg";
import eye from "assets/images/v2/Eye.svg";
import download from "assets/images/v2/Download.svg";
import lock from "assets/images/v2/lock-keyhole-yellow.svg";
import Pill from "components/v2/pill/pill";
import { useSelector } from "react-redux";
import { getCurrentUserId } from "features/authSlice";
import { useDownloadedDocumentsLogMutation, useViewedDocumentsLogMutation } from "pages/dashboard/home/dashboardService";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";

interface DocumentCardProps {
  readonly documentId: string;
  readonly documentName: string;
  readonly documentInfo: string;
  readonly isInvalidDocument: boolean;
  readonly isNoPreview?: boolean;
  readonly handleDocumentViewer?: () => void;
  readonly handleDownLoad?: () => void;
}
export default function DocumentCard({
  documentId,
  documentName,
  documentInfo,
  isInvalidDocument,
  isNoPreview = false,
  handleDocumentViewer,
  handleDownLoad,
}: DocumentCardProps) {
  const userId = useSelector(getCurrentUserId);

  const [updateDownloadedDocuments] = useDownloadedDocumentsLogMutation();
  const [updateViewedDocuments] = useViewedDocumentsLogMutation();

  const onDocumentViewClick = () => {
    if (isNoPreview) {
      return;
    }

    if (handleDocumentViewer) {
      void updateViewedDocuments({ userId, documentIds: [documentId] });
      handleDocumentViewer();
    }
  };

  const onDocumentDownloadClick = () => {
    if (isNoPreview) {
      return;
    }

    if (handleDownLoad) {
      void updateDownloadedDocuments({ userId, documentIds: [documentId] });
      handleDownLoad();
    }
  };

  return (
    <div className={`${styles.container} ${isNoPreview ? styles.restrictedContainer : ""}`}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <div className={styles.docType}>
            <img className={styles.primaryIcon} alt="file type" src={pdf} />
          </div>
          <div className={styles.docInfo}>
            <div className={`${styles.docName} ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="document-file-name" title={documentName}>{documentName}</div>
            <div className={styles.docDateTime}>Uploaded&nbsp;{formatUploadedDateTime(documentInfo)}</div>
          </div>
        </div>
        <div className={styles.btns}>
          {isInvalidDocument && (
            <div className="invalidBtn">
              <Pill text="Invalid" variant="invalid" />
            </div>
          )}
          {isNoPreview ? (
            <div className={styles.accessOnlyBadge}>
              <img src={lock} alt="uploader only" />
              <span className={styles.accessOnlyText}>Uploader access only</span>
            </div>
          ) : (
            <>
              <button type="button" className={styles.viewBtn} onClick={onDocumentViewClick}>
                <div className={styles.icons}>
                  <img className={styles.primaryIcon1} alt="view" src={eye} />
                </div>
              </button>
              <button type="button" className={styles.downloadBtn} onClick={onDocumentDownloadClick}>
                <div className={styles.icons}>
                  <img className={styles.primaryIcon2} alt="download" src={download} />
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function formatUploadedDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  if (hours === 0) hours = 12;

  return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
}
